import { SiteSetupFragmentFragment } from 'data/__generated__';

import { SectionContainer } from 'components/DetailsView/Section';
import styled from 'styled-components';
import { useMemo, useState } from 'react';
import { TotemLightBox, TotemLightboxImage } from 'components/TotemLightBox';

export const TicketSection = ({ siteSetup }: { siteSetup: SiteSetupFragmentFragment }) => {
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [lightboxImages, setLightboxImages] = useState<TotemLightboxImage[]>([]);
    const [initialImageIndex, setInitialImageIndex] = useState(0);

    const ticketsWithPhotos = useMemo(() => {
        return (siteSetup.odooTickets ?? [])
            .map((ticket) => {
                const photosFromActions = (ticket.actions ?? [])
                    .filter((action) => action.photoUrl)
                    .map((action) => ({
                        url: action.photoUrl!,
                        message: action.message,
                    }));

                return {
                    ticket,
                    photos: photosFromActions,
                };
            })
            .filter(({ photos }) => photos.length > 0);
    }, [siteSetup.odooTickets]);

    const handlePhotoClick = (ticketPhotos: { url: string; message: string }[], photoIndex: number) => {
        const images: TotemLightboxImage[] = ticketPhotos.map((photo) => ({
            url: photo.url,
            label: photo.message,
        }));
        setLightboxImages(images);
        setInitialImageIndex(photoIndex);
        setIsLightboxOpen(true);
    };

    if (ticketsWithPhotos.length === 0) {
        return (
            <SectionContainer title="Photos des Tickets" isInitiallyOpen>
                <EmptyMessage>Aucune photo de ticket disponible pour ce rangement.</EmptyMessage>
            </SectionContainer>
        );
    }

    return (
        <SectionContainer title="Photos des Tickets" isInitiallyOpen>
            <>
                <TicketsContainer>
                    {ticketsWithPhotos.map(({ ticket, photos }) => (
                        <TicketCard key={ticket._id}>
                            <TicketHeader>
                                <TicketId>
                                    Ticket Odoo #{ticket.odooTicketId} - {ticket.ticketType}
                                </TicketId>
                            </TicketHeader>
                            <TicketInfo>
                                <InfoRow>
                                    <InfoLabel>Machine:</InfoLabel>
                                    <InfoValue>{ticket.microstoreColumn.name}</InfoValue>
                                </InfoRow>
                                <InfoRow>
                                    <InfoLabel>Description:</InfoLabel>
                                    <InfoValue>{ticket.description}</InfoValue>
                                </InfoRow>
                            </TicketInfo>
                            <PhotosContainer>
                                {photos.map((photo, index) => (
                                    <Photo
                                        key={index}
                                        src={photo.url}
                                        alt={`Photo ${index + 1} du ticket`}
                                        onClick={() => handlePhotoClick(photos, index)}
                                    />
                                ))}
                            </PhotosContainer>
                        </TicketCard>
                    ))}
                </TicketsContainer>
                <TotemLightBox
                    images={lightboxImages}
                    initialImageIndex={initialImageIndex}
                    isOpen={isLightboxOpen}
                    setIsOpen={setIsLightboxOpen}
                />
            </>
        </SectionContainer>
    );
};

const TicketsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const TicketCard = styled.div`
    padding: 15px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const TicketHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const TicketId = styled.span`
    font-weight: 600;
    font-size: 16px;
    color: ${({ theme }) => theme.textColor};
`;

const TicketInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const InfoRow = styled.div`
    display: flex;
    gap: 8px;
    font-size: 14px;
`;

const InfoLabel = styled.span`
    font-weight: 600;
    min-width: 100px;
    color: ${({ theme }) => theme.textColor};
`;

const InfoValue = styled.span`
    color: ${({ theme }) => theme.textColor};
    flex: 1;
`;

const PhotosContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`;

const Photo = styled.img`
    max-width: 200px;
    max-height: 200px;
    object-fit: contain;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
        opacity: 0.8;
    }
`;

const EmptyMessage = styled.div`
    padding: 20px;
    text-align: center;
    color: ${({ theme }) => theme.infoTextColor};
`;
